import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { blogsAPI } from '../../services/api';
import Modal from '../../components/Modal';
import '../admin/ContentManager.css';

const CONTENT_TABS = [
  { key: 'management_staff', label: 'Management en Staff' },
  { key: 'docenten', label: 'Docenten' },
  { key: 'commissies', label: 'Commissies' },
  { key: 'reviews_hbo', label: 'Recensies HBO' },
  { key: 'reviews_masterclasses', label: 'Recensies Masterclasses' },
  { key: 'reviews_masteropleidingen', label: 'Recensies Masteropleidingen' },
  { key: 'flyers', label: 'Flyers' },
  { key: 'brochures', label: 'Brochures' }
];

const Blogs = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', summary: '', content: '', location: 'all' });
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [retainedImageIds, setRetainedImageIds] = useState([]);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, url: '', title: '' });
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!imageFiles.length) {
      setImagePreviews([]);
      return undefined;
    }

    const previews = imageFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file)
    }));

    setImagePreviews(previews);

    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [imageFiles]);

  const resetFormState = () => {
    setForm({ title: '', summary: '', content: '', location: 'all' });
    setImageFiles([]);
    setEditing(null);
    setRetainedImageIds([]);
    setSaveError('');
  };

  const handleImageSelection = (files) => {
    const nextFiles = Array.from(files || []);
    const availableSlots = Math.max(0, 5 - retainedImageIds.length);

    if (nextFiles.length > availableSlots) {
      window.alert(`Je kunt nog maximaal ${availableSlots} foto('s) toevoegen aan dit nieuwsbericht.`);
    }

    setImageFiles(nextFiles.slice(0, availableSlots));
  };

  const loadPosts = async () => {
    const response = await blogsAPI.getAll();
    setPosts(response.posts || []);
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) return;

    if (retainedImageIds.length + imageFiles.length > 5) {
      window.alert('Een nieuwsbericht mag maximaal 5 foto\'s bevatten.');
      return;
    }

    setSaveError('');
    try {
      if (editing?.id) {
        await blogsAPI.update(editing.id, form, imageFiles, { retainedImageIds });
      } else {
        await blogsAPI.create(form, imageFiles);
      }
      resetFormState();
      setShowModal(false);
      await loadPosts();
    } catch (saveErr) {
      setSaveError(saveErr.message || 'Opslaan mislukt. Probeer opnieuw.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`${t('common.confirm')}?`)) return;
    await blogsAPI.delete(id);
    await loadPosts();
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Website Content</h1>
        {(user.role === 'admin' || user.role === 'manager') && (
          <button
            className="btn-primary"
            onClick={() => {
              resetFormState();
              setShowModal(true);
            }}
          >
            {t('blog.add')}
          </button>
        )}
      </div>

      <div className="cm-topbar" role="navigation" aria-label="Website Content navigatie">
        <NavLink
          to="/admin/blogs"
          className={({ isActive }) => `cm-topbar-btn cm-topbar-link${isActive ? ' active' : ''}`}
        >
          Nieuws beheren
        </NavLink>
        {CONTENT_TABS.map((tab) => (
          <NavLink
            key={tab.key}
            to={`/admin/content?tab=${tab.key}`}
            className="cm-topbar-btn cm-topbar-link"
          >
            {tab.label}
          </NavLink>
        ))}
      </div>

      <div className="user-grid">
        {posts.map((post) => (
          <div key={post.id} className="user-card" style={{ display: 'block' }}>
            <h3>{post.title}</h3>
            <p>{post.summary}</p>
            {Array.isArray(post.media) && post.media.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                {post.media.map((image, index) => (
                  <img
                    key={image.id || `${post.id}-${index}`}
                    src={image.url}
                    alt={`${post.title} ${index + 1}`}
                    style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' }}
                    onClick={() => setPreviewModal({ isOpen: true, url: image.url, title: `${post.title} (${index + 1}/${post.media.length})` })}
                  />
                ))}
              </div>
            )}
            <p style={{ whiteSpace: 'pre-wrap' }}>{post.content}</p>
            <small>{post.location}</small>
            {(user.role === 'admin' || user.role === 'manager') && (
              <div className="form-actions" style={{ marginTop: '10px' }}>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setEditing(post);
                    setForm({
                      title: post.title || '',
                      summary: post.summary || '',
                      content: post.content || '',
                      location: post.location || 'all'
                    });
                    setImageFiles([]);
                    setRetainedImageIds(Array.isArray(post.media) ? post.media.map((item) => item.id) : []);
                    setShowModal(true);
                  }}
                >
                  {t('common.edit')}
                </button>
                <button className="btn-delete" onClick={() => handleDelete(post.id)}>{t('common.delete')}</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetFormState();
        }}
        title={editing ? t('common.edit') : t('blog.add')}
      >
        <form onSubmit={handleSave} className="add-form">
          <div className="form-row">
            <input
              className="form-input"
              placeholder={t('common.name')}
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              required
            />
            <select
              className="form-input"
              value={form.location}
              onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
              disabled={user.role === 'manager'}
            >
              <option value="all">{t('common.all')}</option>
              <option value="Paramaribo">Paramaribo</option>
              <option value="Nickerie">Nickerie</option>
            </select>
          </div>
          <textarea
            className="form-textarea"
            placeholder={t('course.description')}
            value={form.summary}
            onChange={(e) => setForm((prev) => ({ ...prev, summary: e.target.value }))}
          />
          <textarea
            className="form-textarea"
            placeholder="Content"
            value={form.content}
            onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
            required
          />
          <div className="form-row" style={{ display: 'block' }}>
            <label htmlFor="blog-image" style={{ display: 'block', marginBottom: '6px' }}>
              Foto's (maximaal 5)
            </label>
            <input
              id="blog-image"
              type="file"
              multiple
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={(e) => handleImageSelection(e.target.files)}
            />
            <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
              Behouden foto's: {retainedImageIds.length} van 5. Nieuwe selectie: {imageFiles.length}.
            </p>
            {editing && Array.isArray(editing.media) && editing.media.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <strong>Huidige foto's</strong>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px', marginTop: '8px' }}>
                  {editing.media.map((image, index) => {
                    const isRetained = retainedImageIds.includes(image.id);
                    return (
                      <div key={image.id || `${editing.id}-${index}`} style={{ border: '1px solid #e5dff0', borderRadius: '10px', padding: '8px', background: '#fff' }}>
                        <img
                          src={image.url}
                          alt={`${editing.title || 'Nieuws'} ${index + 1}`}
                          style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer' }}
                          onClick={() => setPreviewModal({ isOpen: true, url: image.url, title: `${editing.title || 'Nieuws'} (${index + 1}/${editing.media.length})` })}
                        />
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ width: '100%' }}
                          onClick={() => {
                            setRetainedImageIds((prev) => (
                              isRetained ? prev.filter((id) => id !== image.id) : [...prev, image.id]
                            ));
                          }}
                        >
                          {isRetained ? 'Foto verwijderen' : 'Foto behouden'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {imagePreviews.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <strong>Nieuwe foto's</strong>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px', marginTop: '8px' }}>
                  {imagePreviews.map((preview, index) => (
                    <div key={`${preview.name}-${index}`} style={{ border: '1px solid #e5dff0', borderRadius: '10px', padding: '8px', background: '#fff' }}>
                      <img
                        src={preview.url}
                        alt={preview.name}
                        style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer' }}
                        onClick={() => setPreviewModal({ isOpen: true, url: preview.url, title: preview.name })}
                      />
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ width: '100%' }}
                        onClick={() => setImageFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index))}
                      >
                        Verwijder selectie
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {saveError && (
            <p style={{ color: '#b91c1c', margin: '0 0 8px', fontSize: '0.9rem' }}>{saveError}</p>
          )}
          <div className="form-actions">
            <button className="btn-primary" type="submit">{t('common.save')}</button>
            <button
              className="btn-secondary"
              type="button"
              onClick={() => {
                setShowModal(false);
                resetFormState();
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, url: '', title: '' })}
        title={previewModal.title || 'Foto bekijken'}
        size="lg"
      >
        {previewModal.url && (
          <img
            src={previewModal.url}
            alt={previewModal.title || 'Nieuws afbeelding'}
            style={{ width: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: '8px' }}
          />
        )}
      </Modal>
    </div>
  );
};

export default Blogs;
